import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle, CircleNotch, FileArrowUp, Warning, X, XCircle } from '@phosphor-icons/react';
import { useCompany, useWorkspace } from '../App';
import { api, ApiError } from '../lib/api';
import { faNumber, formatBytes, severityLabel, statusLabel } from '../lib/format';
import { Card, EmptyState, InlineNotice, LoadingState, PageHeader, StatusBadge } from '../components/Ui';
import { CustomSelect, SmartDateField } from '../components/SmartForm';
import type { AccountLevel, AccountRow, Dataset, Mapping, QualityIssue, Run } from '../types';

const LEDGER_FIELD_LABELS: Record<string, string> = {
  entryDate: 'تاریخ', documentNumber: 'شماره سند', description: 'شرح',
  accountCode: 'کد حساب', accountName: 'نام حساب',
  debit: 'بدهکار', credit: 'بستانکار', balance: 'مانده',
};

const ACCOUNT_LEVELS: Array<{ key: Exclude<AccountLevel, 'auto'>; label: string; description: string }> = [
  { key: 'general', label: 'کل', description: 'تراز در سطح حساب‌های کل است.' },
  { key: 'subsidiary', label: 'معین', description: 'حساب‌های معین زیر حساب کل قرار می‌گیرند.' },
  { key: 'detail', label: 'تفصیل', description: 'حساب‌های تفصیلی با پدر کل و معین خوانده می‌شوند.' },
];
const ACCOUNT_LEVEL_LABEL_FA: Record<number, string> = { 1: 'کل', 2: 'معین', 3: 'تفصیل' };

interface EvidenceEntry {
  entryId: string;
  fileName: string;
  sheet: string;
  sourceRow: number;
  date: string | null;
  documentNumber: string;
  description: string;
  accountCode: string;
  accountName: string;
  debit: number;
  credit: number;
}

const EVIDENCE_LABELS: Record<string, string> = {
  totalDebit: 'جمع بدهکار', totalCredit: 'جمع بستانکار', difference: 'اختلاف',
  accountCode: 'کد حساب', code: 'کد حساب', account: 'حساب', name: 'نام حساب',
  amount: 'مبلغ', datasets: 'تعداد جدول', date: 'تاریخ', sourceRow: 'ردیف فایل',
};

function recordOf(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown> : null;
}

function entriesFromEvidence(evidence: unknown[]): EvidenceEntry[] {
  const rows: EvidenceEntry[] = [];
  for (const item of evidence) {
    const record = recordOf(item);
    if (!record) continue;
    const nested = Array.isArray(record.occurrences) ? record.occurrences : [record];
    for (const candidate of nested) {
      const entry = recordOf(candidate);
      if (!entry || typeof entry.entryId !== 'string') continue;
      rows.push({
        entryId: entry.entryId,
        fileName: String(entry.fileName ?? ''),
        sheet: String(entry.sheet ?? ''),
        sourceRow: Number(entry.sourceRow ?? 0),
        date: entry.date === null || entry.date === undefined ? null : String(entry.date),
        documentNumber: String(entry.documentNumber ?? ''),
        description: String(entry.description ?? ''),
        accountCode: String(entry.accountCode ?? ''),
        accountName: String(entry.accountName ?? ''),
        debit: Number(entry.debit ?? 0),
        credit: Number(entry.credit ?? 0),
      });
    }
  }
  return rows;
}

function visibleEvidenceValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'number') return faNumber(value, Number.isInteger(value) ? 0 : 2);
  return String(value);
}

function QualityEvidence({
  issue,
  canCorrect,
  busy,
  onCorrect,
}: {
  issue: QualityIssue;
  canCorrect: boolean;
  busy: boolean;
  onCorrect: (
    entry: EvidenceEntry,
    action: 'edit' | 'exclude',
    patch?: Partial<{
      entryDateIso: string | null; documentNumber: string; description: string;
      accountCode: string; accountName: string; debit: number; credit: number;
    }>,
  ) => Promise<void>;
}) {
  const entries = entriesFromEvidence(issue.evidence);
  const [editing, setEditing] = useState<EvidenceEntry | null>(null);
  const [draft, setDraft] = useState<EvidenceEntry | null>(null);
  if (issue.evidence.length === 0) return null;

  const beginEdit = (entry: EvidenceEntry) => { setEditing(entry); setDraft({ ...entry }); };
  const generic = issue.evidence.map(recordOf).filter((item): item is Record<string, unknown> => Boolean(item))
    .filter((item) => !Array.isArray(item.occurrences) && typeof item.entryId !== 'string');

  return (
    <details className="quality-evidence">
      <summary>{entries.length ? `مشاهده ${faNumber(entries.length)} ردیف و اصلاح` : 'مشاهده شواهد'}</summary>
      {entries.length > 0 && (
        <div className="data-table quality-evidence__table">
          <table>
            <thead>
              <tr>
                <th>منبع</th><th>ردیف</th><th>تاریخ</th><th>سند</th><th>حساب</th>
                <th>بدهکار</th><th>بستانکار</th>{canCorrect && <th />}
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <Fragment key={entry.entryId}>
                  <tr>
                    <td><bdi dir="auto">{entry.fileName}</bdi><small><bdi dir="auto">{entry.sheet}</bdi></small></td>
                    <td>{faNumber(entry.sourceRow)}</td>
                    <td dir="ltr">{entry.date ?? '—'}</td>
                    <td dir="ltr">{entry.documentNumber || '—'}</td>
                    <td><bdi dir="ltr">{entry.accountCode || '—'}</bdi><small>{entry.accountName || entry.description || '—'}</small></td>
                    <td dir="ltr">{faNumber(entry.debit)}</td>
                    <td dir="ltr">{faNumber(entry.credit)}</td>
                    {canCorrect && (
                      <td><button className="button button--ghost button--small" disabled={busy} onClick={() => beginEdit(entry)}>اصلاح</button></td>
                    )}
                  </tr>
                  {editing?.entryId === entry.entryId && draft && (
                    <tr className="quality-editor-row">
                      <td colSpan={canCorrect ? 8 : 7}>
                        <div className="quality-editor">
                          <SmartDateField
                            id={`quality-date-${entry.entryId}`}
                            label="تاریخ ردیف"
                            hint="تاریخ اصلاح‌شده به همان سال و دوره مالی وصل می‌شود."
                            value={draft.date ?? ''}
                            onChange={(date) => setDraft((current) => current ? { ...current, date: date || null } : current)}
                          />
                          <label className="quality-editor__field"><span>شماره سند</span><input dir="ltr" value={draft.documentNumber} onChange={(event) => setDraft({ ...draft, documentNumber: event.target.value })} /></label>
                          <label className="quality-editor__field"><span>کد حساب</span><input dir="ltr" value={draft.accountCode} onChange={(event) => setDraft({ ...draft, accountCode: event.target.value })} /></label>
                          <label className="quality-editor__field"><span>نام حساب</span><input value={draft.accountName} onChange={(event) => setDraft({ ...draft, accountName: event.target.value })} /></label>
                          <label className="quality-editor__field"><span>بدهکار</span><input dir="ltr" inputMode="decimal" type="number" min="0" value={draft.debit} onChange={(event) => setDraft({ ...draft, debit: Number(event.target.value) })} /></label>
                          <label className="quality-editor__field"><span>بستانکار</span><input dir="ltr" inputMode="decimal" type="number" min="0" value={draft.credit} onChange={(event) => setDraft({ ...draft, credit: Number(event.target.value) })} /></label>
                        </div>
                        <div className="card-actions">
                          <button
                            className="button button--primary button--small"
                            disabled={busy}
                            onClick={async () => {
                              await onCorrect(entry, 'edit', {
                                entryDateIso: draft.date, documentNumber: draft.documentNumber,
                                accountCode: draft.accountCode, accountName: draft.accountName,
                                debit: draft.debit, credit: draft.credit,
                              });
                              setEditing(null);
                            }}
                          >ثبت اصلاح و اجرای دوباره</button>
                          <button
                            className="button button--ghost button--small"
                            disabled={busy}
                            onClick={async () => { await onCorrect(entry, 'exclude'); setEditing(null); }}
                          >کنار گذاشتن این ردیف</button>
                          <button className="button button--ghost button--small" disabled={busy} onClick={() => setEditing(null)}>انصراف</button>
                        </div>
                        <p className="hint">کنار گذاشتن فقط روی این پردازش اعمال می‌شود؛ فایل اصلی تغییر یا حذف نمی‌شود.</p>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {generic.length > 0 && (
        <ul className="quality-facts">
          {generic.map((record, index) => (
            <li key={index}>
              {Object.entries(record).map(([key, value]) => (
                <span key={key}><b>{EVIDENCE_LABELS[key] ?? key}:</b> <bdi dir="auto">{visibleEvidenceValue(value)}</bdi></span>
              ))}
            </li>
          ))}
        </ul>
      )}
    </details>
  );
}

export function ProcessingPage() {
  const { fiscalYear, periodKey, bootstrap, can, refresh, navigate } = useWorkspace();
  const company = useCompany();
  const [accountLevel, setAccountLevel] = useState<Exclude<AccountLevel, 'auto'> | ''>('');
  const [files, setFiles] = useState<File[]>([]);
  const [run, setRun] = useState<Run | null>(null);
  const [history, setHistory] = useState<Run[]>([]);
  const [mappings, setMappings] = useState<Mapping[]>([]);
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [skippedFiles, setSkippedFiles] = useState<string[]>([]);
  const [issues, setIssues] = useState<QualityIssue[]>([]);
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [accountQuery, setAccountQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(false);
  const [chartDirty, setChartDirty] = useState(false);
  // Corrections a person has made but not yet submitted, keyed by mapping.
  const [edits, setEdits] = useState<Record<string, Record<string, number>>>({});
  const inputRef = useRef<HTMLInputElement>(null);
  const contextRequest = useRef(0);

  const { maxUploadBytes, maxUploadFiles, uploadFormats, stages } = bootstrap.capabilities;
  const accept = uploadFormats.map((format) => `.${format}`).join(',');

  const loadContext = useCallback(async (runId?: string) => {
    const requestId = ++contextRequest.current;
    const companyId = company.id;
    const [runs, mapped, quality, chart] = await Promise.all([
      api.runs(companyId),
      api.mappings(companyId, runId),
      api.quality(companyId, runId),
      api.accounts(companyId),
    ]);

    // Fetched after the batch rather than inside it because on first load the
    // run is whatever `runs` just returned. Asked without a run it answers with
    // every table the company has ever imported, which is not the question this
    // card asks.
    const active = runId ?? runs.runs[0]?.id;
    const tables = active
      ? await api.datasets(companyId, active)
      : { datasets: [], skippedFiles: [] };
    if (contextRequest.current !== requestId) return false;
    setHistory(runs.runs);
    setMappings(mapped.mappings);
    setIssues(quality.issues);
    setAccounts(chart.accounts);
    if (!runId) setRun(runs.runs[0] ?? null);
    setDatasets(tables.datasets);
    setSkippedFiles(tables.skippedFiles);
    return true;
  }, [company.id]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    void loadContext()
      .catch((caught: Error) => { if (!cancelled) setError(caught.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; contextRequest.current += 1; };
  }, [loadContext]);

  // Polling stops the moment the run reaches a terminal state, including
  // awaiting_mapping — which is terminal until a person acts.
  useEffect(() => {
    if (!run || ['succeeded', 'failed', 'cancelled', 'awaiting_mapping'].includes(run.status)) return undefined;
    const timer = window.setInterval(() => {
      void api.run(company.id, run.id)
        .then(async (result) => {
          setRun(result.run);
          if (['succeeded', 'failed', 'awaiting_mapping'].includes(result.run.status)) {
            await loadContext(result.run.id);
            await refresh();
          }
        })
        .catch(() => undefined);
    }, 1_200);
    return () => window.clearInterval(timer);
  }, [company.id, run, loadContext, refresh]);

  function addFiles(incoming: FileList | null) {
    if (!incoming) return;
    if (!accountLevel) {
      setError('پیش از انتخاب فایل، سطح تراز را مشخص کنید: کل، معین یا تفصیل.');
      return;
    }
    const next: File[] = [];
    const rejected: string[] = [];
    for (const file of Array.from(incoming)) {
      const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
      if (!uploadFormats.includes(extension)) { rejected.push(`${file.name} (پسوند پشتیبانی‌نشده)`); continue; }
      if (file.size > maxUploadBytes) { rejected.push(`${file.name} (بزرگ‌تر از ${formatBytes(maxUploadBytes)})`); continue; }
      next.push(file);
    }
    setError(rejected.length ? `این فایل‌ها اضافه نشدند: ${rejected.join('، ')}` : '');
    setFiles((current) => [...current, ...next].slice(0, maxUploadFiles));
  }

  async function start() {
    if (!fiscalYear || !files.length || !accountLevel) return;
    setBusy(true);
    setError('');
    try {
      const result = await api.startRun(company.id, files, fiscalYear.id, periodKey, accountLevel);
      setRun(result.run);
      setFiles([]);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'شروع پردازش انجام نشد.');
    } finally {
      setBusy(false);
    }
  }

  /** The column a field points at right now: the person's answer, else the detector's. */
  function chosenColumn(mapping: Mapping, field: string): number {
    const edited = edits[mapping.id]?.[field];
    if (edited !== undefined) return edited;
    const detected = mapping.fields[field];
    return typeof detected === 'number' ? detected : -1;
  }

  function editMapping(mapping: Mapping, field: string, column: number) {
    setEdits((current) => ({ ...current, [mapping.id]: { ...(current[mapping.id] ?? {}), [field]: column } }));
  }

  async function decideMapping(mapping: Mapping, decision: 'approved' | 'rejected') {
    try {
      // Sent on approval only: rejecting the sheet drops it, so which column was
      // which stops mattering.
      const corrected = decision === 'approved' && edits[mapping.id]
        ? Object.fromEntries(Object.keys(LEDGER_FIELD_LABELS)
          .map((field) => [field, chosenColumn(mapping, field)])
          .filter(([, column]) => (column as number) >= 0))
        : undefined;
      await api.decideMapping(company.id, mapping.id, {
        decision, expectedVersion: mapping.version, fields: corrected as Record<string, number> | undefined,
      });
      setEdits((current) => { const next = { ...current }; delete next[mapping.id]; return next; });
      if (run) {
        const resumed = await api.resumeRun(company.id, run.id);
        setRun(resumed.run);
        await loadContext(resumed.run.id);
      }
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'تصمیم ثبت نشد.');
    }
  }

  /**
   * Take one sheet out of the run, or put it back, then re-run.
   *
   * The re-run is the point. A mapping decision only changes what the next pass
   * reads, so without it this card would say a sheet is out while every figure
   * on every other page still contains it.
   */
  async function setSheetIncluded(dataset: Dataset, included: boolean) {
    if (!dataset.mappingId || dataset.mappingVersion === null || !run) return;
    setBusy(true);
    setError('');
    try {
      await api.decideMapping(company.id, dataset.mappingId, {
        decision: included ? 'reset' : 'rejected',
        expectedVersion: dataset.mappingVersion,
      });
      const resumed = await api.resumeRun(company.id, run.id);
      setRun(resumed.run);
      await loadContext(resumed.run.id);
      await refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'وضعیت این جدول تغییر نکرد.');
    } finally {
      setBusy(false);
    }
  }

  async function reclassify(code: string, category: string) {
    try {
      await api.reclassifyAccount(company.id, code, category);
      setAccounts((await api.accounts(company.id)).accounts);
      setChartDirty(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'طبقه حساب تغییر نکرد.');
    }
  }

  async function rebuildAfterClassification() {
    if (!run) return;
    setBusy(true);
    setError('');
    try {
      const result = await api.resumeRun(company.id, run.id);
      setRun(result.run);
      setChartDirty(false);
      await loadContext(result.run.id);
      await refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'صورت‌های مالی با طبقه‌بندی تازه بازسازی نشد.');
    } finally {
      setBusy(false);
    }
  }

  async function correctEntry(
    entry: EvidenceEntry,
    action: 'edit' | 'exclude',
    patch?: Partial<{
      entryDateIso: string | null; documentNumber: string; description: string;
      accountCode: string; accountName: string; debit: number; credit: number;
    }>,
  ) {
    if (!run) return;
    setBusy(true);
    setError('');
    try {
      const result = await api.correctEntry(company.id, run.id, entry.entryId, { action, patch });
      setRun(result.run);
      await loadContext(result.run.id);
      await refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'اصلاح ردیف ثبت نشد.');
    } finally {
      setBusy(false);
    }
  }

  async function resolveIssue(issue: QualityIssue, status: 'resolved' | 'ignored') {
    try {
      await api.resolveIssue(company.id, issue.id, status);
      setIssues((await api.quality(company.id, run?.id)).issues);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'وضعیت یافته ثبت نشد.');
    }
  }

  if (loading) return <LoadingState label="در حال بارگذاری وضعیت پردازش" />;
  if (!fiscalYear) {
    return <EmptyState title="سال مالی تعریف نشده است" description="برای بارگذاری داده، ابتدا سال مالی این شرکت را بسازید." />;
  }

  // A rejected mapping is decided, not pending: leaving it here put a sheet the
  // person had already set aside back in the approval queue, presented as
  // something nobody had answered yet.
  const pendingMappings = mappings.filter((mapping) =>
    !['approved', 'rejected'].includes(mapping.status) && mapping.confidence < 0.6);
  const pendingDatasetIds = new Set(pendingMappings.map((mapping) => mapping.datasetId));
  const countedSheets = datasets.filter((dataset) =>
    dataset.status !== 'excluded' && dataset.mappingStatus !== 'rejected' && !pendingDatasetIds.has(dataset.id)).length;
  const unclassified = accounts.filter((account) => account.category === 'unclassified');
  const normalizedAccountQuery = accountQuery.trim().toLocaleLowerCase('fa');
  const matchingAccounts = normalizedAccountQuery
    ? accounts.filter((account) => [account.code, account.name, account.parentCode, account.category]
      .some((value) => value.toLocaleLowerCase('fa').includes(normalizedAccountQuery)))
    : accounts;
  const visibleAccounts = matchingAccounts.slice(0, 250);

  return (
    <>
      <PageHeader
        eyebrow="پردازش اطلاعات"
        title="از فایل تا صورت مالی"
        description={`${company.name} — ${fiscalYear.labelFa}`}
      />

      {error && <InlineNotice tone="danger">{error}</InlineNotice>}

      <div className="processing-grid">
        <Card>
          <div className="card-heading"><h2>بارگذاری فایل</h2></div>
          <fieldset className="account-level-picker">
            <legend>این تراز در کدام سطح حساب است؟</legend>
            <p className="hint">پیش از انتخاب فایل مشخص کنید تا کد هر حساب و رابطه پدر و فرزند آن درست ثبت شود.</p>
            <div className="account-level-options">
              {ACCOUNT_LEVELS.map((level) => (
                <label key={level.key} className={accountLevel === level.key ? 'account-level-option account-level-option--active' : 'account-level-option'}>
                  <input
                    type="radio"
                    name="account-level"
                    value={level.key}
                    checked={accountLevel === level.key}
                    onChange={() => { setAccountLevel(level.key); setError(''); }}
                  />
                  <span><strong>{level.label}</strong><small>{level.description}</small></span>
                </label>
              ))}
            </div>
          </fieldset>
          <div
            className={`dropzone ${dragging ? 'dropzone--active' : ''} ${!accountLevel ? 'dropzone--disabled' : ''}`}
            onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => { event.preventDefault(); setDragging(false); addFiles(event.dataTransfer.files); }}
            onClick={() => accountLevel
              ? inputRef.current?.click()
              : setError('ابتدا یکی از سطح‌های کل، معین یا تفصیل را انتخاب کنید.')}
            role="button"
            tabIndex={0}
            aria-disabled={!accountLevel}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              if (accountLevel) inputRef.current?.click();
              else setError('ابتدا یکی از سطح‌های کل، معین یا تفصیل را انتخاب کنید.');
            }}
          >
            <FileArrowUp size={30} />
            <strong>فایل را اینجا رها کنید یا کلیک کنید</strong>
            <small>
              {/* PDF is listed because the reader handles it; a scanned page is
                  refused with a reason rather than parsed into nonsense. */}
              {uploadFormats.join('، ')} — حداکثر {faNumber(maxUploadFiles)} فایل و {formatBytes(maxUploadBytes)} در هر بار
            </small>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={accept}
              hidden
              onChange={(event) => { addFiles(event.target.files); event.target.value = ''; }}
            />
          </div>

          {files.length > 0 && (
            <ul className="file-list">
              {files.map((file, index) => (
                <li key={`${file.name}-${index}`}>
                  <span>{file.name}</span>
                  <small>{formatBytes(file.size)}</small>
                  <button
                    className="icon-button"
                    onClick={() => setFiles((current) => current.filter((_, position) => position !== index))}
                    aria-label={`حذف ${file.name}`}
                  ><X size={15} /></button>
                </li>
              ))}
            </ul>
          )}

          <button
            className="button button--primary button--wide"
            onClick={start}
            disabled={busy || !files.length || !accountLevel || !can('import.upload')}
          >
            {busy ? 'در حال ارسال…' : 'شروع پردازش'}
          </button>
        </Card>

        <Card>
          <div className="card-heading">
            <h2>مراحل پردازش</h2>
            {run && <StatusBadge status={run.status} label={statusLabel(run.status)} />}
          </div>
          {!run ? (
            <p className="hint">هنوز پردازشی برای این شرکت اجرا نشده است.</p>
          ) : (
            <>
              <div className="progress-track"><i style={{ width: `${run.progress}%` }} /></div>
              <ol className="stage-rail">
                {stages.map((definition) => {
                  const step = run.steps.find((item) => item.stage === definition.stage);
                  const status = step?.status ?? 'pending';
                  return (
                    <li key={definition.key} className={`stage stage--${status === 'succeeded' ? 'done' : status === 'running' ? 'active' : status}`}>
                      <span className="stage__icon">
                        {status === 'succeeded' ? <CheckCircle size={17} weight="fill" />
                          : status === 'running' ? <CircleNotch size={17} className="spin" />
                            : status === 'failed' ? <XCircle size={17} weight="fill" />
                              : <span className="stage__number">{faNumber(definition.stage)}</span>}
                      </span>
                      <div>
                        <strong>{definition.labelFa}</strong>
                        {step?.detailFa && <small>{step.detailFa}</small>}
                      </div>
                    </li>
                  );
                })}
              </ol>
              {run.errorFa && <InlineNotice tone="danger">{run.errorFa}</InlineNotice>}
              {run.status === 'awaiting_mapping' && <InlineNotice tone="warning">{run.messageFa}</InlineNotice>}
              {run.status === 'succeeded' && (
                <div className="card-actions" role="group" aria-label="گام بعد از پردازش">
                  <button className="button button--primary button--small" onClick={() => navigate('statements')}>
                    مشاهده صورت‌های مالی
                  </button>
                  <button className="button button--ghost button--small" onClick={() => navigate('reports')}>
                    ساخت گزارش تحلیلی
                  </button>
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      {pendingMappings.length > 0 && (
        <Card>
          <div className="card-heading"><h2>تأیید نگاشت ستون‌ها</h2></div>
          <p className="hint">
            ستون‌های زیر با اطمینان کافی شناسایی نشدند. تا زمانی که تأیید نکنید، هیچ رقمی از این جدول‌ها
            وارد صورت‌های مالی نمی‌شود. اگر ستونی اشتباه تشخیص داده شده، از فهرست روبه‌رویش ستون درست
            فایل را انتخاب کنید؛ انتخاب شما جای تشخیص خودکار را می‌گیرد.
          </p>
          {pendingMappings.map((mapping) => (
            <div key={mapping.id} className="data-table">
              <div className="data-table__head">
                <strong><bdi dir="auto">{mapping.title}</bdi> — <bdi dir="auto">{mapping.sheet}</bdi></strong>
                <span className="data-badge">اطمینان {faNumber(mapping.confidence * 100)}٪</span>
              </div>
              <table>
                <thead><tr><th>ستون استاندارد</th><th>ستون فایل</th></tr></thead>
                <tbody>
                  {Object.entries(LEDGER_FIELD_LABELS).map(([field, label]) => (
                    <tr key={field}>
                      <td>{label}</td>
                      <td>
                        <CustomSelect
                          ariaLabel={`ستون فایل برای ${label}`}
                          value={String(chosenColumn(mapping, field))}
                          disabled={!can('mapping.approve')}
                          onChange={(value) => editMapping(mapping, field, Number(value))}
                          options={[
                            { value: '-1', label: 'پیدا نشد' },
                            ...mapping.columns.map((column, index) => ({
                              value: String(index),
                              label: column || `ستون ${faNumber(index + 1)}`,
                              // One column cannot be two things at once, and a
                              // mapping that says it is would import the same
                              // figure twice.
                              disabled: Object.keys(LEDGER_FIELD_LABELS)
                                .some((other) => other !== field && chosenColumn(mapping, other) === index),
                            })),
                          ]}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {mapping.assumptions.map((assumption) => (
                <p key={assumption} className="hint">{assumption}</p>
              ))}
              <div className="card-actions">
                <button className="button button--primary button--small" disabled={!can('mapping.approve')} onClick={() => decideMapping(mapping, 'approved')}>تأیید و ادامه</button>
                <button className="button button--ghost button--small" disabled={!can('mapping.approve')} onClick={() => decideMapping(mapping, 'rejected')}>رد این جدول</button>
              </div>
            </div>
          ))}
        </Card>
      )}

      {accounts.length > 0 && (
        <Card>
          <div className="card-heading">
            <h2>ساختار و طبقه‌بندی حساب‌ها</h2>
            <span className="data-badge">
              {faNumber(accounts.length)} حساب
              {unclassified.length ? `؛ ${faNumber(unclassified.length)} طبقه‌بندی‌نشده` : ''}
            </span>
          </div>
          <p className="hint">
            سطح کل، معین یا تفصیل و حساب پدر از کد حساب ساخته شده است. طبقه هر حساب را می‌توانید
            اصلاح کنید؛ برای اعمال تغییر در صورت‌ها، دکمه بازسازی را بزنید.
          </p>
          <label className="account-chart-search">
            <span>جست‌وجوی حساب</span>
            <input
              type="search"
              value={accountQuery}
              onChange={(event) => setAccountQuery(event.target.value)}
              placeholder="کد، نام، حساب پدر یا طبقه"
            />
          </label>
          <div className="data-table">
            <table>
              <thead><tr><th>کد</th><th>نام حساب</th><th>سطح</th><th>حساب پدر</th><th>طبقه</th></tr></thead>
              <tbody>
                {visibleAccounts.map((account) => (
                  <tr key={account.code}>
                    <td dir="ltr">{account.code}</td>
                    <td>{account.name}</td>
                    <td>{ACCOUNT_LEVEL_LABEL_FA[account.level] ?? '—'}</td>
                    <td dir="ltr">{account.parentCode || '—'}</td>
                    <td>
                      <CustomSelect
                        ariaLabel={`طبقه حساب ${account.name}`}
                        value={account.category}
                        onChange={(category) => reclassify(account.code, category)}
                        disabled={!can('mapping.edit')}
                        options={bootstrap.capabilities.accountCategories.map((category) => ({
                          value: category.key, label: category.labelFa,
                        }))}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {matchingAccounts.length === 0 && <p className="hint">حسابی با این جست‌وجو پیدا نشد.</p>}
          {matchingAccounts.length > visibleAccounts.length && (
            <p className="hint">
              برای روان ماندن صفحه، {faNumber(visibleAccounts.length)} حساب نخست نمایش داده شده است؛ جست‌وجو را دقیق‌تر کنید.
            </p>
          )}
        </Card>
      )}

      {chartDirty && (
        <InlineNotice tone="warning">
          <span>طبقه‌بندی حساب تغییر کرده است. تا اجرای دوباره، صورت‌های مالی هنوز بر پایه طبقه‌بندی قبلی هستند.</span>
          <button className="button button--primary button--small" disabled={busy} onClick={rebuildAfterClassification}>
            {busy ? 'در حال بازسازی…' : 'اعمال اصلاحات و بازسازی صورت‌ها'}
          </button>
        </InlineNotice>
      )}

      {/* Placed immediately above «کیفیت داده» because the finding that sends a
          person looking — «ارقام دو جدول روی هم افتاده است» — is the first thing
          in that card, and the answer to it is this table. */}
      {run && datasets.length > 0 && (
        <Card>
          <div className="card-heading">
            <h2>جدول‌های این پردازش</h2>
            <span className="data-badge">{faNumber(countedSheets)} از {faNumber(datasets.length)} جدول در صورت‌ها</span>
          </div>
          <p className="hint">
            ارقام صورت‌های مالی فقط از جدول‌های زیر می‌آید. یک تراز آزمایشی همه حساب‌های دوره را دارد،
            پس اگر صورت سود و زیان هم بارگذاری شده باشد ارقام دو بار شمرده می‌شود؛ در آن صورت یکی را
            کنار بگذارید. کنار گذاشتن یا بازگرداندن یک جدول، پردازش را دوباره اجرا می‌کند.
          </p>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>فایل</th><th>جدول</th><th>ردیف‌ها</th><th>وضعیت</th>
                  {can('mapping.approve') && <th />}
                </tr>
              </thead>
              <tbody>
                {datasets.map((dataset) => {
                  const excluded = dataset.status === 'excluded' || dataset.mappingStatus === 'rejected';
                  const waiting = pendingDatasetIds.has(dataset.id);
                  return (
                    <tr key={dataset.id}>
                      {/* `bdi` so a latin file name cannot drag the row's
                          punctuation across it. */}
                      <td><bdi dir="auto">{dataset.fileName}</bdi></td>
                      <td><bdi dir="auto">{dataset.sheet}</bdi></td>
                      <td>{faNumber(dataset.acceptedRowCount)} از {faNumber(dataset.sourceRowCount)}</td>
                      <td>
                        <span className="data-badge">
                          {excluded ? 'کنار گذاشته شده' : waiting ? 'در انتظار تأیید نگاشت' : 'وارد صورت‌ها شده'}
                        </span>
                      </td>
                      {can('mapping.approve') && (
                        <td>
                          <button
                            className="button button--ghost button--small"
                            disabled={busy || !dataset.mappingId}
                            onClick={() => setSheetIncluded(dataset, excluded)}
                          >
                            {excluded ? 'بازگرداندن' : 'کنار گذاشتن'}
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {skippedFiles.length > 0 && (
            <p className="hint">
              {/* Four files in and two tables out is not an answer to «کدام
                  فایل‌هایم خوانده نشد». */}
              از این فایل‌ها هیچ جدول حساب‌داری خوانده نشد و ارقامشان وارد صورت‌های مالی نشده است:{' '}
              {skippedFiles.map((name, index) => (
                <span key={name}>{index > 0 && '، '}<bdi dir="auto">{name}</bdi></span>
              ))}.
            </p>
          )}
        </Card>
      )}

      <Card>
        <div className="card-heading">
          <h2>کیفیت داده</h2>
          <span className="data-badge">{faNumber(issues.filter((issue) => issue.status === 'open').length)} یافته باز</span>
        </div>
        {issues.length === 0 ? (
          <p className="hint">برای آخرین پردازش هیچ یافته‌ای ثبت نشده است.</p>
        ) : (
          <ul className="alert-list">
            {issues.map((issue) => (
              <li key={issue.id}>
                <span className={`severity severity--${issue.severity}`}>{severityLabel(issue.severity)}</span>
                <div>
                  <strong>{issue.titleFa} <small className="data-badge">{faNumber(issue.count)} مورد</small></strong>
                  <p>{issue.descriptionFa}</p>
                  <QualityEvidence
                    issue={issue}
                    canCorrect={can('quality.resolve') && Boolean(run)}
                    busy={busy}
                    onCorrect={correctEntry}
                  />
                  {issue.status === 'open' && can('quality.resolve') && (
                    <div className="card-actions">
                      <button className="button button--ghost button--small" onClick={() => resolveIssue(issue, 'resolved')}>رسیدگی شد</button>
                      <button className="button button--ghost button--small" onClick={() => resolveIssue(issue, 'ignored')}>نادیده بگیر</button>
                    </div>
                  )}
                  {issue.status !== 'open' && <span className="data-badge">{statusLabel(issue.status)}</span>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {history.length > 0 && (
        <Card>
          <div className="card-heading"><h2>تاریخچه پردازش‌ها</h2></div>
          <div className="report-history">
            {history.map((item) => (
              <button key={item.id} className={run?.id === item.id ? 'active' : ''} onClick={() => { setRun(item); void loadContext(item.id); }}>
                <strong>{statusLabel(item.status)}</strong>
                <small>{item.messageFa || `${faNumber(item.progress)}٪`}</small>
                {item.status === 'failed' && <Warning size={15} />}
              </button>
            ))}
          </div>
        </Card>
      )}
    </>
  );
}
